import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { withPather } from "react-pather";
import TagsTopBar from "./TagsTopBar";
import TagRow from "./TagRow";
import { SortKey } from "./TagsSortKeySelect";
import { Page, Pagination, SearchInput } from "../../general";
import { useEffectExceptOnMount, useQueryParam } from "../../../hooks";

const QueryParam = {
  PERPAGE: "perPage",
  OFFSET: "offset",
  SORTKEY: "sortKey",
};

const DefaultSettings = {
  perPage: 25,
  offset: 0,
  sortKey: SortKey.CONVERSIONS_AMOUNT,
};

const Tags = ({ pather }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null); // For Tag Rows

  const [queryLoading, setQueryLoading] = useState(false);
  const timerRef = useRef(null);

  const [tags, setTags] = useState([]);
  const [tagsTotalPages, setTagsTotalPages] = useState(0);
  const [tagsNumOfResults, setTagsNumOfResults] = useState(0);

  const { setQueryParam, getQueryParam } = useQueryParam(DefaultSettings);

  // Query Params Control
  const [query, setQuery] = useState("");

  const setPerPage = (val) => setQueryParam(QueryParam.PERPAGE, val);
  const setOffset = (val) => setQueryParam(QueryParam.OFFSET, val);
  const setSortKey = (val) => setQueryParam(QueryParam.SORTKEY, val);

  const setMethods = {
    setPerPage,
    setOffset,
    setSortKey,
  };

  const getPerPage = () => getQueryParam(QueryParam.PERPAGE);
  const getOffset = () => getQueryParam(QueryParam.OFFSET);
  const getSortKey = () => getQueryParam(QueryParam.SORTKEY);

  const settings = {
    perPage: getPerPage(),
    offset: getOffset(),
    sortKey: getSortKey(),
  };

  const { perPage, offset, sortKey } = settings;

  // FIXME: use one fetchTag everywhere (through DI)
  const fetchTags = (queryChanged = false, success = null) => {
    if (queryChanged) {
      // Clear the previous timer if it exists
      if (timerRef.current) clearTimeout(timerRef.current);
      // Set a new timer to show loader if query takes longer than 2 seconds
      timerRef.current = setTimeout(() => {
        setQueryLoading(true); // Show loader after 2 seconds
      }, 2000);
    } else setLoading(true);

    axios
      .get(pather.back.tags, {
        params: {
          query,
          perPage,
          offset,
          sortKey,
        },
      })
      .then((response) => {
        const data = response.data;
        setTags(data.tags);
        setTagsTotalPages(data.total_pages);
        setTagsNumOfResults(data.number_of_results);
        success && success();
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar("Some error occurred during tags fetching", {
          variant: "error",
        });
      })
      .finally(() => {
        if (queryChanged) {
          // Clear the timeout and stop loading
          setQueryLoading(false);
          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }
        } else {
          setLoading(false);
        }
      });
  };

  const onSuccessfulCreation = (tag) => {
    fetchTags(false, () => {
      setOffset(0);
    });
  };

  const updateTagLocally = (updatedTag) => {
    setTags((currentTags) =>
      currentTags.map((tag) => (tag.code === updatedTag.code ? updatedTag : tag))
    );
  };

  const deleteTagLocally = (deletedTagCode) => {
    const pageLength = tags.length - 1;
    setTags((currentTags) =>
      currentTags.filter((tag) => tag.code !== deletedTagCode)
    );
    setTagsNumOfResults(tagsNumOfResults - 1);
    if (pageLength === 0 && offset !== 0) {
      setOffset(offset - 1);
    }
  };

  const clean = () => {
    setTagsNumOfResults(0);
  };

  const reload = () => {
    clean();
    fetchTags(false, () => {
      setOffset(0);
    });
  };

  const resetSettings = () => navigate(location.pathname, { replace: true });

  const handleOffsetChange = (event, value) => setOffset(value - 1);

  useEffect(() => {
    fetchTags();
  }, []);

  useEffectExceptOnMount(() => {
    fetchTags();
  }, [offset]);

  useEffectExceptOnMount(() => {
    fetchTags(false, () => {
      setOffset(0);
    });
  }, [perPage, sortKey]);

  useEffectExceptOnMount(() => {
    fetchTags(true, () => {
      setOffset(0);
    });
  }, [query]);

  return (
    <Page label="Tags" loading={loading}>
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <SearchInput
          placeholder="Search tags"
          query={query}
          onChange={(e) => setQuery(e.target.value.trim())}
          onClear={() => setQuery("")}
          loading={queryLoading}
        />
        <Box
          sx={{
            mt: 2,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <TagsTopBar
            tagsNumOfResults={tagsNumOfResults}
            reload={reload}
            settings={settings}
            resetSettings={resetSettings}
            setMethods={setMethods}
            onSuccessfulCreation={onSuccessfulCreation}
          />
          <Box>
            {tags && tags.length > 0 ? (
              tags.map((tag, index) => (
                <TagRow
                  key={index}
                  index={index}
                  tag={tag}
                  query={query}
                  sortKey={sortKey}
                  updateTagLocally={updateTagLocally}
                  deleteTagLocally={deleteTagLocally}
                  hoveredIndex={hoveredIndex}
                  setHoveredIndex={setHoveredIndex}
                />
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  minHeight: "275px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box className="h3-style">No results</Box>
              </Box>
            )}
          </Box>
          {tagsTotalPages > 1 && (
            <Box
              sx={{
                width: "100%",
                mt: 3,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                offset={offset}
                totalPages={tagsTotalPages}
                handleOffsetChange={handleOffsetChange}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Page>
  );
};

export default withPather()(Tags);

export { DefaultSettings };
